/**
 * The page's side of tracking. Runs the models in public/js/track-worker.js
 * when this browser can, and on the page when it cannot, with one interface:
 *
 *   const t = createTrackClient({ ... onResult });
 *   await t.ready            'worker' or 'main'
 *   t.submit(video, ms, opts) -> false when a frame is still being read
 *   t.close()
 *
 * ONE FRAME AT A TIME. Nothing ever queues: while the models are reading a
 * frame, newer frames are not sent, and the caller sends the newest one the
 * moment the result is back. A queue is how a slow phone falls seconds behind
 * the player; dropping keeps the overlay on what is happening now.
 *
 * FALLBACK. A worker that cannot start (no WebGL in workers, which is Safari
 * before 17), that goes quiet while it starts, or that stops answering mid
 * session is replaced by the same engine on the page, without ending the
 * session: a worker that fails never takes the tracking down with it. Only a
 * browser that cannot run the models at all, on the page either, ends up with
 * none, and then the page says so. ?track=main forces the page path, for
 * comparing the two.
 *
 * SLOW IS NOT DEAD. The first frame is allowed far longer than the others:
 * the GPU compiles the models' shaders on it, which took 1 to 24 seconds in
 * WebKit on 2026-09-25 and 30 to 60 in Chrome with software graphics. The
 * hand models are built after the pose model, while frames already run
 * (trackengine.js): a frame may wait BUILD_MS while they are being built, and
 * the first frame that runs them has the first frame's allowance. Otherwise a
 * frame may take twenty times the worker's own recent frames, and never less
 * than eight seconds, before the worker counts as gone. A worker that is merely
 * slow stays: the page would be just as slow running the models itself, and
 * frozen while it did.
 */
import { createEngine, TRACK_PROTOCOL } from './trackengine.js';

const WORKER_URL = '/js/track-worker.js';
const STALL_MS = 8000;           // a frame out this long (or twenty of its recent frames) means the worker is gone
const FIRST_FRAME_MS = 120000;   // ...except the first, which compiles the GPU shaders
// Building the pose model, once its bytes are in, took about 2 seconds with a
// Mac's GPU and 1 to 4 with software graphics (2026-09-25; the first frame
// then compiles its shaders, above). A worker still not ready this long after
// the bytes are in has hung (a GPU that will not start in a worker), and the
// page builds the models instead.
const BUILD_MS = 45000;
// Before that, the worker loads MediaPipe and downloads the models, and says
// so at each step. The download has no limit of its own (it is the network,
// and the page would wait on the same bytes), but a worker that says nothing
// at all for this long has hung, and the page takes over.
const QUIET_MS = 45000;
// ?trackfail=gpu: every frame on the page path with the GPU fails, to test that a broken GPU is
// noticed and the tracking moves to the processor with a message (scripts/weak-check.mjs). Local test servers only.
const FAIL_GPU = (() => { try { return /^(localhost|127\.0\.0\.1)$/.test(location.hostname) && new URLSearchParams(location.search).get('trackfail') === 'gpu'; } catch { return false; } })();
const FORCE = (() => { try { const v = new URLSearchParams(location.search).get('track'); return v === 'main' || v === 'worker' ? v : null; } catch { return null; } })();

export function createTrackClient({ visionUrl, loadVision, model = 'full', numPoses = 4, hands = true, delegate = 'GPU', onResult, onPath = () => {} }) {
  let worker = null, engine = null, path = null, busy = false, seq = 0, closed = false, workerHands = false;
  let sentAt = 0, stallTimer = null, errors = 0, answered = 0, frameMs = 0;
  // The hand models: being built in the worker right now, whether a frame has
  // run them yet, and whether the frame out now is the first that does (it
  // compiles their shaders).
  let handsBuilding = false, handsWarm = false, firstHandsOut = false;
  // When each step of starting up happened, ms after this client was made:
  // what a slow start is spent on, for scripts/perf-track.mjs.
  const t0 = performance.now();
  const timings = {};
  const mark = (k) => { if (timings[k] === undefined) timings[k] = Math.round(performance.now() - t0); };

  function stopWorker() {
    clearInterval(stallTimer); stallTimer = null;
    if (worker) { try { worker.terminate(); } catch {} worker = null; }
  }

  async function startMain() {
    mark('mainStart');
    const vision = await loadVision();
    const fileset = await vision.FilesetResolver.forVisionTasks(`${visionUrl}/wasm`);
    engine = await createEngine({ vision, fileset, model, numPoses, hands, delegate, onStage: (st) => mark(`main_${st}`) });
    mark('mainReady');
    return engine;
  }

  function startWorker() {
    return new Promise((resolve, reject) => {
      let w, buildTimer = null, quietTimer = null, settled = false;
      try { w = new Worker(WORKER_URL); } catch (e) { reject(e); return; }
      worker = w;
      const fail = (msg) => { if (settled) return; settled = true; clearTimeout(buildTimer); clearTimeout(quietTimer); reject(new Error(msg)); };
      // Any word from the worker while it starts up restarts the quiet clock.
      const heard = () => { clearTimeout(quietTimer); if (!settled) quietTimer = setTimeout(() => fail('worker went quiet while starting'), QUIET_MS); };
      heard();
      w.onerror = (e) => { e.preventDefault?.(); if (path === 'worker') toMain(`worker error: ${e.message || 'unknown'}`); else fail(e.message || 'worker failed to start'); };
      w.onmessageerror = () => { if (path === 'worker') toMain('worker message error'); };
      w.onmessage = (e) => {
        const m = e.data || {};
        if (m.type === 'stage') {
          mark(m.stage);
          // The hand models are built after the pose model, while frames run.
          if (m.stage === 'handsBuild') handsBuilding = true;
          if (m.stage === 'hands') { workerHands = Boolean(m.hands); handsBuilding = false; }
          if (m.stage === 'fetched') { clearTimeout(quietTimer); clearTimeout(buildTimer); buildTimer = setTimeout(() => fail('worker did not finish building the models'), BUILD_MS); }
          else heard();
          return;
        }
        if (m.type === 'ready') { mark('ready'); settled = true; clearTimeout(buildTimer); clearTimeout(quietTimer); resolve(m); return; }
        if (m.type === 'error' && m.stage === 'init') { fail(m.message); return; }
        if (m.type === 'result') {
          busy = false; errors = 0;
          if (Number.isFinite(m.ms?.hands)) handsWarm = true;
          if (!answered++) mark('firstResult');
          else if (Number.isFinite(m.ms?.total)) frameMs = frameMs ? frameMs + 0.2 * (m.ms.total - frameMs) : m.ms.total;
          m.lat = performance.now() - m.t; m.path = 'worker'; deliver(m); return;
        }
        if (m.type === 'error') {
          busy = false;
          // One bad frame is a bad frame. Several in a row is a broken worker.
          if (++errors >= 5) toMain(`worker frames failing: ${m.message}`);
        }
      };
      w.postMessage({ type: 'init', proto: TRACK_PROTOCOL, visionUrl, model, numPoses, hands, delegate });
    });
  }

  function deliver(r) {
    try { onResult?.(r); } catch (e) { console.error('tracking result', e); }
  }

  /** The worker died or hung mid session: the page takes over, the session goes on. */
  let switching = null;
  function toMain(why) {
    if (switching || closed) return switching;
    console.warn('tracking moves to the page:', why);
    stopWorker();
    path = null; busy = false;
    switching = startMain().then(() => { path = 'main'; onPath(path, why); }).catch((e) => { console.error('tracking could not start on the page', e); onPath(null, String(e?.message || e)); });
    return switching;
  }

  const ready = (async () => {
    const canWorker = FORCE !== 'main' && typeof Worker === 'function' && typeof createImageBitmap === 'function' && typeof OffscreenCanvas === 'function';
    if (canWorker) {
      try {
        const m = await startWorker();
        path = 'worker'; workerHands = Boolean(m.hands);
        // A frame that never comes back means the worker is gone: the GPU
        // process was reset, or the tab ran out of memory.
        stallTimer = setInterval(() => {
          const limit = !answered || firstHandsOut ? FIRST_FRAME_MS
            : handsBuilding ? BUILD_MS
            : Math.max(STALL_MS, 20 * frameMs);
          if (busy && path === 'worker' && sentAt && performance.now() - sentAt > limit) toMain('worker stopped answering');
        }, 1000);
        onPath(path, m);
        return path;
      } catch (e) {
        console.warn('tracking worker unavailable, running on the page:', e?.message || e);
        stopWorker();
      }
    }
    await startMain();
    path = 'main';
    onPath(path, null);
    return path;
  })();

  /**
   * A picture of the current video frame the worker can own: an ImageBitmap.
   * Not a VideoFrame, though it is cheaper to make: handed to the worker, it
   * stopped the camera outright in 2 of 3 runs on 2026-09-25 (no frames at
   * all, the tracking stuck at zero), where bitmaps never did.
   */
  function capture(video, W, H, maxSide) {
    // Scaled on the way out when the job does not need every camera pixel (a close player, or the
    // processor path): a smaller picture to hand over and for the model to read. The engine maps it
    // back, since it is told the camera frame's own size (opts.W, opts.H).
    const s = maxSide ? Math.min(1, maxSide / Math.max(W, H)) : 1;
    if (s >= 1) return createImageBitmap(video);
    return createImageBitmap(video, { resizeWidth: Math.round(W * s), resizeHeight: Math.round(H * s), resizeQuality: 'medium' });
  }

  return {
    ready,
    get path() { return path; },
    get busy() { return busy; },
    get timings() { return { ...timings }; },
    get hasHands() { return path === 'main' ? Boolean(engine?.hasHands) : path === 'worker' && workerHands; },

    /**
     * Read this frame of `video`. Returns false, and does nothing, while the
     * last frame is still being read. `t` must rise from one call to the next.
     */
    submit(video, t, opts = {}) {
      if (busy || closed || !path) return false;
      const W = video.videoWidth, H = video.videoHeight;
      if (!W || !H) return false;
      busy = true;
      if (path === 'main') {
        let r = null;
        try { if (FAIL_GPU && delegate === 'GPU') throw new Error('simulated GPU failure'); r = engine.process(video, t, { ...opts, W, H }); errors = 0; }
        catch (e) {
          busy = false; console.warn('tracking frame', e?.message || e);
          // A broken engine (a lost GPU context) fails every frame: after a run of them the page is told,
          // instead of a camera that silently shows nothing.
          if (++errors === 10) { path = null; onPath(null, `frames failing: ${e?.message || e}`); }
          return false;
        }
        busy = false;
        if (!answered++) mark('firstResult');
        r.lat = performance.now() - t; r.path = 'main';
        deliver(r);
        return true;
      }
      const id = ++seq;
      const w = worker;
      firstHandsOut = Boolean(opts.hands) && workerHands && !handsWarm;
      const t0 = performance.now();
      capture(video, W, H, opts.maxSide).then((frame) => {
        if (closed || worker !== w || path !== 'worker') { try { frame.close(); } catch {} busy = false; return; }
        sentAt = performance.now();
        w.postMessage({ type: 'frame', id, t, frame, opts: { ...opts, W, H }, captureMs: sentAt - t0 }, [frame]);
      }).catch((e) => { busy = false; console.warn('frame capture', e?.message || e); });
      return true;
    },

    close() {
      closed = true;
      if (worker) { try { worker.postMessage({ type: 'close' }); } catch {} }
      setTimeout(stopWorker, 200);
      try { engine?.close(); } catch {}
      engine = null;
    },
  };
}
